import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft } from "@phosphor-icons/react";
import { ApplicantMessagePanel } from "../components/ApplicantMessagePanel";

const SKILLS = [
  "Can start immediately", "Available evenings", "Has a valid driver's license", "Has current auto insurance",
  "Is a CNA (active)", "Has full TB documentation", "Referred by an employee",
  "Available weekends", "Has reliable transportation", "Has a reliable vehicle",
  "Has verifiable caregiving experience", "First Aid/CPR certified (current)", "Friend/family caregiver", "Retiree",
];
const QUALITIES = [
  "Warm, empathetic tone",
  "Active listener (answers the question asked, asks clarifying questions when needed)",
  "Clear and consistent communication",
  "Understands the caregiving role and expectations",
  "Engaged and motivated (shows interest in clients, schedule, training, reliability)",
  "Accountability and reliability mindset",
];
const NO_REASONS = [
  "Did not respond after two call attempts", "Unprofessional communication or behavior",
  "Poor understanding or low interest", "Availability does not meet needs", "Requirements not met",
  "Does not meet screening requirements (includes drug screen)", "Other",
  "Did not respond to email after 72 hours", "Responses unclear or inconsistent", "Reliability concerns",
  "Transportation or location barrier", "Compensation expectations misaligned", "Overall not a strong fit at this time",
];
const RECS = [
  { v: "yes", label: "Yes — schedule interview (sends the confirmation)" },
  { v: "fast_track", label: "FAST TRACK — start reference & employer checks now; offer only after the interview" },
  { v: "no_90", label: "No — may apply again in 90 days" },
  { v: "no_decline", label: "No — decline (sends the declined email)" },
];

const BRING_DOCS = ["Photo ID (driver's license or state ID)", "Social Security card", "TB test results",
  "Child abuse clearance", "CNA registration", "Passport or green card", "Any certificates or awards"];

const Check = ({ on, label, onToggle, testid }) => (
  <button type="button" onClick={onToggle} data-testid={testid}
    className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${on ? "border-primary bg-primary/10 text-stone-800" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40"}`}>
    <span className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border ${on ? "border-primary bg-primary" : "border-stone-300 bg-white"}`} />
    <span className="leading-snug">{label}</span>
  </button>
);

const F = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</label>
    {children}
  </div>
);

export default function PrescreenPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ available_start: "", why_apply: "", experience_summary: "", notes: "", suitability_notes: "", recommendation: "", interview_date: "", interview_time: "", screener: "", offer_pay_rate: "", offer_start_date: "", offer_schedule: "" });
  const [skills, setSkills] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [bring, setBring] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showMsg, setShowMsg] = useState(false);

  const load = () => api.get(`/applicants/${aid}`).then((r) => {
      const d = r.data;
      setA(d);
      const ps = d.prescreen || {};
      setF({
        available_start: ps.available_start || d.start_date || "",
        why_apply: ps.why_apply || d.most_important || d.how_heard || "",
        experience_summary: ps.experience_summary || [d.experience_years, (d.certifications || []).join(", ")].filter(Boolean).join(" · "),
        notes: ps.notes || "", suitability_notes: ps.suitability_notes || "",
        recommendation: ps.recommendation || "",
        interview_date: (d.interview_at || "").split(" ")[0] || "", interview_time: (d.interview_at || "").split(" ")[1] || "",
        screener: ps.screener || "Patti",
      });
      setSkills(ps.skills || []);
      setQualities(ps.qualities || []);
      setReasons(ps.no_reasons || []);
    }).catch((e) => setErr(formatApiError(e)));

  useEffect(() => { load(); }, [aid]);

  const tog = (list, set) => (v) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const isYes = f.recommendation === "yes";
  const isFast = f.recommendation === "fast_track";
  const isNo = f.recommendation === "no_90" || f.recommendation === "no_decline";
  const valid = f.recommendation && f.screener.trim()
    && (!isYes || (f.interview_date && f.interview_time));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    const action = f.recommendation === "no_decline" ? "decline" : f.recommendation === "no_90" ? "defer" : f.recommendation === "fast_track" ? "fast_track" : "schedule";
    try {
      await api.put(`/applicants/${aid}/decision`, {
        action,
        interview_at: isYes ? `${f.interview_date} ${f.interview_time}` : "",
        docs_to_bring: isYes ? bring : [],
        prescreen: {
          available_start: f.available_start, why_apply: f.why_apply, experience_summary: f.experience_summary,
          skills, qualities, notes: f.notes, suitability_notes: f.suitability_notes,
          recommendation: f.recommendation, no_reasons: reasons, screener: f.screener.trim(),
        },
      });
      toast.success(action === "schedule" ? "Interview scheduled — confirmation email sent to the applicant"
        : action === "fast_track" ? "Fast track — reference & employer checks sent; offer goes out after the interview"
        : `${a.name} ${action}d — email sent`);
      nav("/hiring");
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (err) return (
    <div className="mx-auto max-w-3xl p-8" data-testid="prescreen-error">
      <div className="rounded-xl border border-stone-200 bg-white p-8 text-center shadow-sm">
        <p className="text-sm font-semibold text-stone-800">This application is no longer available</p>
        <p className="mt-2 text-xs leading-relaxed text-stone-500">
          The link may be from an older email, or the application was already processed or removed. Open Hiring to see the current list.
        </p>
        <Link to="/hiring" className="mt-4 inline-block rounded-full bg-primary px-5 py-2 text-xs font-semibold text-primary-foreground hover:bg-stone-900" data-testid="prescreen-error-back">Go to Hiring</Link>
      </div>
    </div>
  );
  if (!a) return <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="prescreen-page">
      <div>
        <Link to="/hiring" className="inline-flex items-center gap-1 text-xs font-medium text-stone-400 hover:text-primary" data-testid="prescreen-back"><ArrowLeft size={13} /> Back to Hiring</Link>
        <h1 className="mt-1 font-heading text-3xl font-bold text-stone-900">Applicant Pre-Screening</h1>
        <p className="mt-1 text-sm text-stone-500">
          {a.name} · <a className="text-primary hover:underline" href={`tel:${a.phone}`}>{a.phone || "no phone"}</a> · <a className="text-primary hover:underline" href={`mailto:${a.email}`}>{a.email}</a>
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link to={`/hiring/application/${aid}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-1.5 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="prescreen-view-application">
            View full application
          </Link>
          <button onClick={() => setShowMsg((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-1.5 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-900 hover:text-white"
            data-testid="prescreen-message-btn">
            Send a message
          </button>
        </div>
        {showMsg && <div className="mt-3"><ApplicantMessagePanel applicant={a} onSent={load} testid="prescreen-message" /></div>}
        {a.status !== "new" && (
          <p className="mt-2 rounded-lg bg-[#B7791F]/10 px-3 py-2 text-xs text-[#8a5d14]" data-testid="prescreen-already">
            This application was already prescreened (status: {a.status.replace("_", " ")}). Submitting again will re-run the decision and resend the email.
          </p>
        )}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 text-xs text-stone-600 shadow-sm" data-testid="prescreen-summary">
        <p className="font-semibold text-stone-800">From the application</p>
        <p className="mt-1">
          Availability: {(a.availability || []).join(", ") || "—"}{a.availability_notes ? ` · ${a.availability_notes}` : ""} ·
          Eligible to work in US: {a.eligible_us || "—"} · License+insurance: {a.has_license_insurance || "—"} · Vehicle: {a.reliable_vehicle || "—"}
        </p>
        <p className="mt-1">
          {a.criminal_attest ? ` Criminal record question: ${a.criminal_attest}${a.criminal_attest_detail ? ` — ${a.criminal_attest_detail}` : ""} ·` : ""}
          Employers: {(a.employers || []).length} · References: {(a.references || []).length}
          {a.referral_code ? ` · Referral code: ${a.referral_code}` : ""}
        </p>
        {a.pa_resident_2y && (
          <p className="mt-1" data-testid="prescreen-pa-resident">
            Lived in PA continuously 2+ years: <span className={`font-semibold ${a.pa_resident_2y === "Yes" ? "text-[#1e8449]" : "text-[#C0392B]"}`}>{a.pa_resident_2y}</span>
            {a.pa_resident_2y !== "Yes" && " — FBI fingerprint background check will be required before starting"}
          </p>
        )}
        {(a.scenario_help || a.why_notice || a.why_reliable || a.why_person) && (
          <div className="mt-2 space-y-1.5 border-t border-stone-100 pt-2" data-testid="prescreen-scenarios">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Their words — they took time on these. Acknowledge them on the call and let the conversation build from here.</p>
            {a.scenario_help && <p><span className="font-semibold text-stone-700">Helped when they didn't have to:</span> {a.scenario_help}</p>}
            {a.why_notice && <p><span className="font-semibold text-stone-700">Why noticing small changes matters:</span> {a.why_notice}</p>}
            {a.why_reliable && <p><span className="font-semibold text-stone-700">Why reliability matters:</span> {a.why_reliable}</p>}
            {a.why_person && <p><span className="font-semibold text-stone-700">Tasks vs. caring:</span> {a.why_person}</p>}
          </div>
        )}
      </div>

      <div className="space-y-5 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Available to start"><Input type="date" value={f.available_start} onChange={set("available_start")} className="border-stone-300" data-testid="prescreen-start" /></F>
          <F label="Screener — completed by"><Input value={f.screener} onChange={set("screener")} placeholder="First & last name" className="border-stone-300" data-testid="prescreen-screener" /></F>
        </div>
        <F label="Why did you apply for this position?">
          <Textarea rows={3} value={f.why_apply} onChange={set("why_apply")} className="border-stone-300" data-testid="prescreen-why" />
        </F>
        <F label="Tell me about your caregiving experience (paid or unpaid)">
          <Textarea rows={3} value={f.experience_summary} onChange={set("experience_summary")} className="border-stone-300" data-testid="prescreen-experience" />
        </F>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Select all that apply</p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {SKILLS.map((s, i) => <Check key={s} on={skills.includes(s)} label={s} onToggle={() => tog(skills, setSkills)(s)} testid={`prescreen-skill-${i}`} />)}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Select all that apply — interpersonal</p>
          <div className="grid gap-1.5">
            {QUALITIES.map((s, i) => <Check key={s} on={qualities.includes(s)} label={s} onToggle={() => tog(qualities, setQualities)(s)} testid={`prescreen-quality-${i}`} />)}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Notes"><Textarea rows={3} value={f.notes} onChange={set("notes")} className="border-stone-300" data-testid="prescreen-notes" /></F>
          <F label="Suitability notes"><Textarea rows={3} value={f.suitability_notes} onChange={set("suitability_notes")} className="border-stone-300" data-testid="prescreen-suitability" /></F>
        </div>

        <div className="border-t border-stone-100 pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Recommended for interview? <span className="text-primary">*</span></p>
          <div className="grid gap-1.5 sm:grid-cols-2" data-testid="prescreen-rec">
            {RECS.map((r) => (
              <button key={r.v} type="button" onClick={() => setF((x) => ({ ...x, recommendation: r.v }))} data-testid={`prescreen-rec-${r.v}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-xs font-medium transition-colors ${f.recommendation === r.v ? "border-primary bg-primary/10 text-stone-900" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40"}`}>
                <span className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 ${f.recommendation === r.v ? "border-primary bg-primary" : "border-stone-300 bg-white"}`} />
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {isNo && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">If no, why?</p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {NO_REASONS.map((s, i) => <Check key={s} on={reasons.includes(s)} label={s} onToggle={() => tog(reasons, setReasons)(s)} testid={`prescreen-reason-${i}`} />)}
            </div>
          </div>
        )}

        {isYes && (
          <div className="grid gap-4 rounded-lg bg-[#27AE60]/5 p-4 sm:grid-cols-2" data-testid="prescreen-interview-when">
            <F label="Interview date"><Input type="date" value={f.interview_date} onChange={set("interview_date")} className="border-stone-300 bg-white" data-testid="prescreen-date" /></F>
            <F label="Interview time"><Input type="time" value={f.interview_time} onChange={set("interview_time")} className="border-stone-300 bg-white" data-testid="prescreen-time" /></F>
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Tell them to bring — this goes in their confirmation</p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {BRING_DOCS.map((d, i) => <Check key={d} on={bring.includes(d)} label={d} onToggle={() => tog(bring, setBring)(d)} testid={`prescreen-bring-${i}`} />)}
              </div>
            </div>
          </div>
        )}

        {isFast && (
          <div className="rounded-lg bg-[#27AE60]/5 p-4" data-testid="prescreen-fasttrack-note">
            <p className="text-xs leading-relaxed text-stone-600">
              Fast track starts the reference checks and employer verification <b>now</b>, because we intend to hire if
              everything checks out. The face-to-face interview still has to happen — and the <b>offer letter goes out
              only after the interview</b>.
            </p>
          </div>
        )}

        <Button onClick={submit} disabled={busy || !valid}
          className="h-11 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground hover:bg-stone-900" data-testid="prescreen-submit">
          {busy ? "Submitting…" : isYes ? "Submit & send interview confirmation" : isFast ? "Submit — start reference & employer checks" : isNo ? "Submit & send the email" : "Submit pre-screening"}
        </Button>
        {!valid && f.recommendation && (
          <p className="text-center text-[11px] text-stone-400" data-testid="prescreen-hint">
            {isYes && !(f.interview_date && f.interview_time) ? "Pick the interview date & time" : !f.screener.trim() ? "Enter who completed this screening" : ""}
          </p>
        )}
      </div>
    </div>
  );
}
